import { z } from "zod";
import { useStore } from "../store";
import type { CraneParameters } from "../types";
import { createAlertFromDefinition } from "../data/alerts";
import { AlertDefinitionKey } from "../data/keys";

// Minimal internal models parsed from Markdown
const ConditionSchema = z.union([
  z.object({ event: z.string(), target: z.string().optional() }),
  z.object({ action: z.string(), payload: z.any().optional() }),
  z.object({ selector: z.string() }),
]);

const CompletionSchema = z.object({
  any: z.array(ConditionSchema).optional(),
  all: z.array(ConditionSchema).optional(),
});

const StepMetaSchema = z.object({
  id: z.string(),
  autoAdvance: z.boolean().optional(),
  skippable: z.boolean().optional(),
  timeoutMs: z.number().optional(),
  completeWhen: CompletionSchema.optional(),
  hints: z.array(z.string()).optional(),
});

export type StepMeta = z.infer<typeof StepMetaSchema>;

// Event registry
type EventHandler = (detail: any) => void;
type DomainKey = string; // e.g., "ui.opened"

type StepEventTrigger = { event: string; target?: string; tolerance?: number };
type ParsedStep = {
  id: string;
  title: string;
  bodyHtml: string;
  events?: StepEventTrigger[];
  completeAll?: StepEventTrigger[];
  completeAny?: StepEventTrigger[];
  controls?: string[];
  highlight?: string;
};
type ParsedWalkthrough = { id: string; title?: string; steps: ParsedStep[] };

class WalkthroughService {
  private handlers: Map<DomainKey, Set<EventHandler>> = new Map();
  private unsubscribeStore?: () => void;
  private lastCrane?: CraneParameters;
  private mhLoadAttachSlew30AlertShown: boolean = false;
  private controlsTrainingResetDone: boolean = false;
  private controlsTrainingAlertShown: boolean = false;
  private lastStepId?: string;
  private stepEnteredAtMs?: number;
  private stepTimerHandle?: number;

  // Debug utilities
  private isDebug(): boolean {
    try {
      // Enable by setting localStorage WT_DEBUG or wt.debug to "1" or setting window.__WT_DEBUG__ = true
      return (
        (typeof window !== "undefined" &&
          ((window as any).__WT_DEBUG__ === true ||
            window.localStorage?.getItem("WT_DEBUG") === "1" ||
            window.localStorage?.getItem("wt.debug") === "1")) ||
        false
      );
    } catch {
      return false;
    }
  }
  private debug(...args: any[]) {
    if (!this.isDebug()) return;
    try {
      // eslint-disable-next-line no-console
      console.debug("[WalkthroughService]", ...args);
    } catch {}
  }

  start(id: string) {
    const { startWalkthrough } = useStore.getState();
    startWalkthrough(id);
    this.loadAndSet(id).finally(() => this.subscribeStore());

    // Scenario-specific initialization
    try {
      const state: any = useStore.getState();
      if (id === "mh-load-attach") {
        // Initialize MH load to 0 at the beginning of this scenario
        if (typeof state.updateCraneParameter === "function") {
          state.updateCraneParameter("mhLoad", 0);
        }
        // Reset one-off alert trigger for this scenario
        this.mhLoadAttachSlew30AlertShown = false;
      }
      if (id === "controls-training") {
        // Reset flags for this scenario
        this.controlsTrainingResetDone = false;
        this.controlsTrainingAlertShown = false;
      }
    } catch {}
  }

  stop() {
    const { stopWalkthrough } = useStore.getState();
    stopWalkthrough();
    this.unsubscribeStore?.();
    this.unsubscribeStore = undefined;
    // Clear any active step timer
    try {
      if (this.stepTimerHandle !== undefined) {
        window.clearTimeout(this.stepTimerHandle);
        this.stepTimerHandle = undefined;
      }
    } catch {}
  }

  next() {
    useStore.getState().nextStep();
  }

  prev() {
    useStore.getState().prevStep();
  }

  restart() {
    useStore.getState().restartWalkthrough();
  }

  registerEvent(domain: DomainKey, handler: EventHandler) {
    if (!this.handlers.has(domain)) this.handlers.set(domain, new Set());
    this.handlers.get(domain)!.add(handler);
    return () => this.handlers.get(domain)!.delete(handler);
  }

  emit(domain: DomainKey, detail?: any) {
    this.debug("emit", { domain, detail });
    const set = this.handlers.get(domain);
    if (set) {
      set.forEach((h) => h(detail));
    }
    this.evaluateAutoAdvance(domain, detail);
  }

  // Subscribe to store updates for simple selector conditions and crane events
  private subscribeStore() {
    const { subscribe } = useStore;
    this.unsubscribeStore?.();
    this.unsubscribeStore = subscribe(
      (s) => [
        s.currentModal,
        s.showNotifications,
        s.showLayoutSelector,
        s.widgets,
        // React to any crane parameter change via object identity
        s.craneParameters,
        s.currentId,
        s.stepIndex,
        s.steps,
      ],
      () => {
        // Emit crane-domain events for walkthrough steps
        try {
          const { craneParameters } = useStore.getState() as unknown as {
            craneParameters: CraneParameters;
          };
          if (!craneParameters) return;

          const prev = this.lastCrane || craneParameters;

          // Generic per-parameter change events
          Object.keys(craneParameters).forEach((key) => {
            const k = key as keyof CraneParameters;
            const currVal = craneParameters[k] as unknown as string | number;
            const prevVal = (prev as any)[k] as string | number | undefined;
            if (currVal !== prevVal) {
              this.emit(`crane.${String(k)}.changed`, {
                key: String(k),
                value: currVal,
                previous: prevVal,
                target: String(currVal),
              });
            }
          });

          // Scenario convenience events (kept for compatibility)
          const { slewAngle, boomAngle, absTravel, speed, slewSpeed } =
            craneParameters;

          if (slewAngle !== prev.slewAngle) {
            const rounded = Math.round(slewAngle);
            this.emit("crane.slew.changed", { target: String(rounded) });

            // Scenario-specific: during mh-load-attach, raise a HIGH_WIND alert when crossing 30° once
            try {
              const { currentId, addAlert } =
                useStore.getState() as unknown as {
                  currentId: string | null;
                  addAlert: (a: import("../types").Alert) => void;
                };
              if (
                currentId === "mh-load-attach" &&
                !this.mhLoadAttachSlew30AlertShown &&
                typeof prev.slewAngle === "number" &&
                prev.slewAngle < 30 &&
                slewAngle >= 30
              ) {
                const base = createAlertFromDefinition(
                  AlertDefinitionKey.HIGH_WIND_SPEED
                );
                const alert = {
                  id: `alert-${Date.now()}-${Math.random()
                    .toString(36)
                    .substr(2, 9)}`,
                  ...base,
                } as import("../types").Alert;
                addAlert(alert);
                this.mhLoadAttachSlew30AlertShown = true;
              }
            } catch {}
          }

          if (boomAngle !== prev.boomAngle) {
            const rounded = Math.round(boomAngle);
            this.emit("crane.boom.changed", { target: String(rounded) });
          }

          if (absTravel !== prev.absTravel) {
            this.debug("absTravel changed", {
              previous: prev.absTravel,
              current: absTravel,
            });
            if (absTravel >= 10 && (prev.absTravel as number) < 10) {
              this.emit("crane.absTravel.reached", { target: "10" });
            }
            if (absTravel >= 20 && (prev.absTravel as number) < 20) {
              this.emit("crane.absTravel.reached", { target: "20" });
            }
          }

          if (speed !== prev.speed) {
            if (speed === 0 && (prev.speed as number) !== 0) {
              this.emit("crane.speed.zero", { target: "0" });
            }
          }

          // Slew direction convenience events based on slewSpeed sign
          if (slewSpeed !== prev.slewSpeed) {
            if ((slewSpeed as number) > 0) {
              this.emit("crane.slew.cw", { target: ">0" });
            } else if ((slewSpeed as number) < 0) {
              this.emit("crane.slew.ccw", { target: "<0" });
            } else {
              this.emit("crane.slew.stop", { target: "0" });
            }
          }

          // Boom direction convenience events based on boomSpeed sign
          if (craneParameters.boomSpeed !== prev.boomSpeed) {
            if ((craneParameters.boomSpeed as number) > 0) {
              this.emit("crane.boom.up", { target: ">0" });
            } else if ((craneParameters.boomSpeed as number) < 0) {
              this.emit("crane.boom.down", { target: "<0" });
            } else {
              this.emit("crane.boom.stop", { target: "0" });
            }
          }

          // Hoist direction convenience events based on speed sign
          if (speed !== prev.speed) {
            if ((speed as number) > 0) {
              this.emit("crane.hoist.up", { target: ">0" });
            } else if ((speed as number) < 0) {
              this.emit("crane.hoist.down", { target: "<0" });
            } else {
              this.emit("crane.hoist.stop", { target: "0" });
            }
          }

          this.lastCrane = { ...craneParameters };
        } catch {}

        // Scenario-specific: When reaching the 'reset-params' step, reset crane parameters once
        try {
          const {
            currentId,
            stepIndex,
            steps,
            resetCraneParameters,
            addAlert,
          } = useStore.getState() as unknown as {
            currentId: string | null;
            stepIndex: number;
            steps?: ParsedStep[];
            resetCraneParameters: () => void;
            addAlert: (a: import("../types").Alert) => void;
          };
          // Update highlighted element for the current step, if any
          try {
            const current =
              steps && steps.length > 0
                ? steps[Math.max(0, Math.min(stepIndex, steps.length - 1))]
                : undefined;
            const nextHighlight = current?.highlight || null;
            useStore.setState({ highlightedElement: nextHighlight });
          } catch {}

          // Handle step change bookkeeping and timer-based events
          try {
            const current =
              steps && steps.length > 0
                ? steps[Math.max(0, Math.min(stepIndex, steps.length - 1))]
                : undefined;
            const currentStepId = current?.id;
            if (currentStepId !== this.lastStepId) {
              // Step changed
              this.lastStepId = currentStepId || undefined;
              this.stepEnteredAtMs = Date.now();
              // Clear any existing timer
              if (this.stepTimerHandle !== undefined) {
                try {
                  window.clearTimeout(this.stepTimerHandle);
                } catch {}
                this.stepTimerHandle = undefined;
              }
              // Schedule timer if step defines a timer completion
              if (current) {
                const timers: number[] = [];
                const collectTimers = (arr?: StepEventTrigger[]) => {
                  arr?.forEach((t) => {
                    if (t.event === "timer") {
                      const n = Number(t.target);
                      if (!Number.isNaN(n) && n >= 0) timers.push(n);
                    }
                  });
                };
                collectTimers(current.completeAny);
                collectTimers(current.completeAll);
                if (timers.length > 0) {
                  const seconds = Math.min(...timers);
                  this.debug("schedule timer", {
                    seconds,
                    stepId: currentStepId,
                  });
                  this.stepTimerHandle = window.setTimeout(() => {
                    this.debug("timer elapsed", {
                      seconds,
                      stepId: currentStepId,
                    });
                    this.emit("timer", { target: String(seconds) });
                  }, seconds * 1000);
                }
              }
            }
          } catch {}
          if (
            currentId === "controls-training" &&
            !this.controlsTrainingResetDone
          ) {
            const current =
              steps && steps.length > 0
                ? steps[Math.max(0, Math.min(stepIndex, steps.length - 1))]
                : undefined;
            if (current && current.id === "reset-params") {
              resetCraneParameters?.();
              this.controlsTrainingResetDone = true;
            }
          }

          // Scenario-specific: When reaching the 'incoming-alert' step, generate a HIGH_WIND_SPEED alert once
          try {
            if (
              currentId === "controls-training" &&
              !this.controlsTrainingAlertShown
            ) {
              const current =
                steps && steps.length > 0
                  ? steps[Math.max(0, Math.min(stepIndex, steps.length - 1))]
                  : undefined;
              if (current && current.id === "incoming-alert") {
                const base = createAlertFromDefinition(
                  AlertDefinitionKey.HIGH_WIND_SPEED
                );
                const alert = {
                  id: `alert-${Date.now()}-${Math.random()
                    .toString(36)
                    .substr(2, 9)}`,
                  ...base,
                } as import("../types").Alert;
                addAlert(alert);
                this.controlsTrainingAlertShown = true;
                useStore.getState().updateCraneParameter("windSpeed", 30);
              }
            }
          } catch {}
        } catch {}
      },
      { equalityFn: shallowArrayEqual }
    );
  }

  // (Progress persistence removed; walkthrough progress is not persisted)

  // -------- Loading & Parsing (minimal) --------
  async loadAndSet(id: string) {
    try {
      const doc = await loadWalkthrough(id);
      const { steps } = doc;
      useStore.setState({ steps, stepIndex: 0 });
      // Emit initial UI event so first step with ui.opened can auto-advance
      this.emit("ui.opened", { target: "operation" });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  }

  // Minimal evaluator (events only for now) with debug logging
  private evaluateAutoAdvance(domain: string, detail: any) {
    const state = useStore.getState() as unknown as {
      steps?: ParsedStep[];
      stepIndex: number;
      nextStep: () => void;
      currentId: string | null;
      updateCraneParameter?: (key: string, value: any) => void;
      craneParameters: CraneParameters;
    };
    const { steps, stepIndex } = state;

    if (!steps || steps.length === 0) {
      return;
    }
    const current = steps[Math.max(0, Math.min(stepIndex, steps.length - 1))];
    this.debug("evaluateAutoAdvance: start", {
      domain,
      detail,
      stepIndex,
      stepId: current?.id,
      stepTitle: current?.title,
    });

    const events = (current as any).events as StepEventTrigger[] | undefined;
    const completeAll = (current as any).completeAll as
      | StepEventTrigger[]
      | undefined;
    const completeAny = (current as any).completeAny as
      | StepEventTrigger[]
      | undefined;

    // Evaluate completeWhen any/all using current state snapshot (takes precedence)
    if (completeAll || completeAny) {
      const matchesTarget = (
        trig: StepEventTrigger,
        value: string,
        prevValue?: string
      ) => {
        if (!trig.target) return true;
        const tol = trig.tolerance ?? 0;
        const numTarget = Number(trig.target);
        const numValue = Number(value);
        if (!Number.isNaN(numTarget) && !Number.isNaN(numValue)) {
          // Threshold semantics for "reached" events: consider satisfied when value >= target - tol
          if (trig.event.endsWith(".reached")) {
            if (prevValue === undefined) {
              return numValue >= numTarget - tol;
            }
            const numPrev = Number(prevValue);
            if (Number.isNaN(numPrev)) {
              return numValue >= numTarget - tol;
            }
            const lower = numTarget - tol;
            const upper = numTarget + tol;
            const crossedUp = numPrev < lower && numValue >= lower;
            const crossedDown = numPrev > upper && numValue <= upper;
            return crossedUp || crossedDown;
          }
          // Default numeric comparison within tolerance window
          return Math.abs(numValue - numTarget) <= tol;
        }
        return (
          String(trig.target).toLowerCase() === String(value).toLowerCase()
        );
      };

      // Build current values we can compare against for known events
      const values: Record<string, string> = {};
      const p = state.craneParameters;
      const prevP = this.lastCrane || p;
      values["crane.slew.changed"] = String(p.slewAngle);
      values["crane.boom.changed"] = String(p.boomAngle);
      values["crane.absTravel.reached"] = String(p.absTravel);
      values["crane.speed.zero"] = p.speed === 0 ? "0" : String(p.speed);

      const prevValues: Record<string, string> = {};
      prevValues["crane.slew.changed"] = String((prevP as any).slewAngle);
      prevValues["crane.boom.changed"] = String((prevP as any).boomAngle);
      prevValues["crane.absTravel.reached"] = String((prevP as any).absTravel);
      prevValues["crane.speed.zero"] =
        (prevP as any).speed === 0 ? "0" : String((prevP as any).speed);

      const valuesBool: Record<string, boolean> = {
        "crane.slew.cw": (p.slewSpeed ?? 0) > 0,
        "crane.slew.ccw": (p.slewSpeed ?? 0) < 0,
        "crane.slew.stop": (p.slewSpeed ?? 0) === 0,
        "crane.boom.up": (p.boomSpeed ?? 0) > 0,
        "crane.boom.down": (p.boomSpeed ?? 0) < 0,
        "crane.boom.stop": (p.boomSpeed ?? 0) === 0,
        "crane.hoist.up": p.speed > 0,
        "crane.hoist.down": p.speed < 0,
        "crane.hoist.stop": p.speed === 0,
      };

      this.debug("evaluateAutoAdvance: completion spec", {
        completeAll,
        completeAny,
        values,
        prevValues,
        valuesBool,
      });

      // First, allow direct event satisfaction when a completion spec exists (e.g., alerts.cycledAll)
      if (detail && typeof detail === "object") {
        const eventMatches = (t: StepEventTrigger) => {
          if (t.event !== domain) return false;
          if (!t.target) return true;
          return (
            String(t.target).toLowerCase() ===
            String(detail.target || "").toLowerCase()
          );
        };
        if (completeAny && completeAny.some(eventMatches)) {
          this.debug("advance: completion satisfied by direct event match", {
            domain,
            detail,
          });
          state.nextStep();
          return;
        }
      }

      const elapsedSeconds = this.stepEnteredAtMs
        ? (Date.now() - this.stepEnteredAtMs) / 1000
        : 0;

      const conditionOk = (t: StepEventTrigger): boolean => {
        if (t.event === "timer") {
          const target = Number(t.target);
          if (Number.isNaN(target)) return false;
          return elapsedSeconds >= target;
        }
        return t.event in values
          ? matchesTarget(t, values[t.event], prevValues[t.event])
          : valuesBool[t.event] === true;
      };

      const anyOk = completeAny ? completeAny.some(conditionOk) : false;

      const allOk = completeAll ? completeAll.every(conditionOk) : false;

      // Per-trigger diagnostics
      if (completeAny) {
        completeAny.forEach((t) => {
          const v =
            t.event in values ? values[t.event] : String(valuesBool[t.event]);
          const pv = t.event in prevValues ? prevValues[t.event] : undefined;
          const ok =
            t.event in values
              ? matchesTarget(t, values[t.event], pv)
              : valuesBool[t.event] === true;
          this.debug("completeAny check", {
            trigger: t,
            value: v,
            prev: pv,
            ok,
          });
        });
      }
      if (completeAll) {
        completeAll.forEach((t) => {
          const v =
            t.event in values ? values[t.event] : String(valuesBool[t.event]);
          const pv = t.event in prevValues ? prevValues[t.event] : undefined;
          const ok =
            t.event in values
              ? matchesTarget(t, values[t.event], pv)
              : valuesBool[t.event] === true;
          this.debug("completeAll check", {
            trigger: t,
            value: v,
            prev: pv,
            ok,
          });
        });
      }

      if (allOk || anyOk) {
        this.debug("advance: completion satisfied", { anyOk, allOk });
        state.nextStep();
        return;
      }
    }

    // Event-driven advance (legacy support) - only if no completion spec present
    if (
      !completeAll &&
      !completeAny &&
      detail &&
      typeof detail.target === "string"
    ) {
      const evtTarget = String(detail.target).toLowerCase();
      if (
        events &&
        events.some(
          (e) =>
            e.event === domain &&
            (!e.target || e.target.toLowerCase() === evtTarget)
        )
      ) {
        this.debug("advance: event-driven", { domain, target: evtTarget });
        state.nextStep();
        // Scenario-specific side-effects after advancing
        try {
          if (
            state.currentId === "mh-load-attach" &&
            domain === "crane.speed.zero"
          ) {
            // Attach fictional load to hook
            state.updateCraneParameter?.("mhLoad", 1200);
          }
        } catch {}
        return;
      }
    }
    // fallback heuristic for legacy steps
    if (domain === "ui.opened") {
      const uiTarget =
        detail && typeof detail.target === "string"
          ? String(detail.target).toLowerCase()
          : "";
      if (
        (uiTarget && current.id.includes(uiTarget)) ||
        (uiTarget && current.title.toLowerCase().includes(uiTarget))
      ) {
        this.debug("advance: ui.opened heuristic", { uiTarget });
        state.nextStep();
      }
    }
  }
}

export const walkthroughService = new WalkthroughService();

// Shallow equality helper for tuple state
function shallowArrayEqual(a: unknown[], b: unknown[]) {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

// -------- Loading & Parsing (minimal) --------

async function fetchMarkdown(id: string): Promise<string> {
  const res = await fetch(`/walkthroughs/${id}.md`, { cache: "no-store" });
  if (!res.ok) throw new Error(`Failed to load walkthrough ${id}`);
  return await res.text();
}

function parseFrontmatter(md: string): {
  fm: Record<string, any>;
  body: string;
} {
  if (md.startsWith("---")) {
    const end = md.indexOf("\n---", 3);
    if (end > 0) {
      const raw = md.slice(3, end).trim();
      const body = md.slice(end + 4);
      const fm: Record<string, any> = {};
      raw.split(/\r?\n/).forEach((line) => {
        const m = line.match(/^([a-zA-Z0-9_]+):\s*(.*)$/);
        if (m) fm[m[1]] = parseYamlScalar(m[2]);
      });
      return { fm, body };
    }
  }
  return { fm: {}, body: md };
}

function parseYamlScalar(v: string): any {
  const trimmed = v.trim();
  if (trimmed === "true") return true;
  if (trimmed === "false") return false;
  const num = Number(trimmed);
  if (!Number.isNaN(num)) return num;
  if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
    try {
      return JSON.parse(
        trimmed.replace(/([a-zA-Z0-9_]+)(?=\s*[\],])/g, '"$1"')
      );
    } catch {}
  }
  return trimmed.replace(/^"|"$/g, "");
}

function parseSteps(bodyMd: string): ParsedStep[] {
  const sections = bodyMd.split(/^###\s+Step:\s+/m).slice(1);
  return sections.map((section) => {
    const [titleLine, ...rest] = section.split(/\r?\n/);
    const content = rest.join("\n");
    // Extract fenced walkthrough block (optional)
    const metaMatch = content.match(/```walkthrough[\s\S]*?```/);
    const meta = metaMatch ? metaMatch[0] : null;
    const idMatch = meta ? meta.match(/id:\s*([^\n]+)/) : null;
    const id = idMatch
      ? idMatch[1].trim()
      : titleLine.trim().toLowerCase().replace(/\s+/g, "-");
    const bodyHtml = markdownToBasicHtml(
      content.replace(meta || "", "").trim()
    );
    const events = meta ? parseMetaEvents(meta) : undefined;
    const completion = meta ? parseMetaCompletion(meta) : undefined;
    const controls = meta ? parseMetaControls(meta) : undefined;
    const highlight = meta ? parseMetaHighlight(meta) : undefined;
    return {
      id,
      title: titleLine.trim(),
      bodyHtml,
      events,
      completeAll: completion?.all,
      completeAny: completion?.any,
      controls,
      highlight,
    };
  });
}

function markdownToBasicHtml(md: string): string {
  // Minimal: paragraphs + code fences + inline emphasis
  let html = md
    .replace(
      /```([\s\S]*?)```/g,
      (_m, code) => `<pre><code>${escapeHtml(code)}</code></pre>`
    ) // code blocks
    .replace(/^\s*-\s+(.*)$/gm, "<li>$1</li>")
    .replace(
      /\n(<li>.*<\/li>)(\n<li>.*<\/li>)+/g,
      (m) => `<ul>${m.replace(/\n/g, "")}</ul>`
    ) // group lis
    .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.*?)\*/g, "<em>$1</em>")
    .replace(/\n\n+/g, "</p><p>");
  html = `<p>${html}</p>`;
  return html;
}

function escapeHtml(s: string) {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

async function loadWalkthrough(id: string): Promise<ParsedWalkthrough> {
  const md = await fetchMarkdown(id);
  const { fm, body } = parseFrontmatter(md);
  const steps = parseSteps(body);
  return { id: String(fm.id || id), title: fm.title, steps };
}

function parseMetaEvents(metaBlock: string): StepEventTrigger[] | undefined {
  // Extract simple event entries under completeWhen any/all
  // Matches lines like: "event: ui.opened" optionally followed by a target line
  const lines = metaBlock.split(/\r?\n/).map((l) => l.trim());
  const events: StepEventTrigger[] = [];
  for (let i = 0; i < lines.length; i++) {
    const ev = lines[i].match(/^-?\s*event:\s*([^#]+?)\s*$/);
    if (ev) {
      const eventName = ev[1].trim();
      // lookahead a few lines for target and tolerance
      let target: string | undefined;
      let tolerance: number | undefined;
      for (let j = i + 1; j < Math.min(i + 6, lines.length); j++) {
        const t = lines[j].match(/^-?\s*target:\s*([^#]+?)\s*$/);
        if (t) {
          target = t[1].trim();
          continue;
        }
        const tol = lines[j].match(/^-?\s*tolerance:\s*([^#]+?)\s*$/);
        if (tol) {
          const num = Number(tol[1].trim());
          if (!Number.isNaN(num)) tolerance = num;
          continue;
        }
        // stop if a new top-level key starts
        if (/^[a-zA-Z]+:/.test(lines[j]) || /^-\s*event:/.test(lines[j])) break;
      }
      events.push({ event: eventName, target, tolerance });
    }
  }
  return events.length ? events : undefined;
}

function parseMetaControls(metaBlock: string): string[] | undefined {
  const lines = metaBlock.split(/\r?\n/).map((l) => l.trim());
  let inline: string | null = null;
  let startIndex = -1;
  for (let i = 0; i < lines.length; i++) {
    const m = lines[i].match(/^-?\s*controls:\s*(.*)$/);
    if (m) {
      inline = m[1]?.trim() || "";
      startIndex = i + 1;
      break;
    }
  }
  if (inline === null) return undefined;
  const results: string[] = [];
  if (inline && inline !== "|") {
    // Support comma or bracket list e.g. [slew, boom] or slew, boom
    const raw = inline
      .replace(/^\[/, "")
      .replace(/\]$/, "")
      .split(/[,\s]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    raw.forEach((r) => results.push(r.toLowerCase()));
  } else if (startIndex >= 0) {
    for (let j = startIndex; j < Math.min(startIndex + 10, lines.length); j++) {
      const li = lines[j];
      if (/^[a-zA-Z]+:/.test(li)) break;
      const item = li.match(/^[-*]\s*([a-zA-Z0-9_+]+)\s*$/);
      if (item) results.push(item[1].toLowerCase());
    }
  }
  return results.length ? results : undefined;
}

function parseMetaHighlight(metaBlock: string): string | undefined {
  const lines = metaBlock.split(/\r?\n/).map((l) => l.trim());
  for (let i = 0; i < lines.length; i++) {
    const m = lines[i].match(/^-?\s*highlight:\s*([^#]+?)\s*$/);
    if (m) {
      return m[1].trim();
    }
  }
  return undefined;
}

function parseMetaCompletion(
  metaBlock: string
): { any?: StepEventTrigger[]; all?: StepEventTrigger[] } | undefined {
  const lines = metaBlock.split(/\r?\n/).map((l) => l.trim());
  let inCompletion = false;
  let mode: "any" | "all" | null = null;
  const any: StepEventTrigger[] = [];
  const all: StepEventTrigger[] = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (/^completeWhen:\s*$/.test(line)) {
      inCompletion = true;
      mode = null;
      continue;
    }
    if (!inCompletion) continue;
    if (/^any:\s*$/.test(line)) {
      mode = "any";
      continue;
    }
    if (/^all:\s*$/.test(line)) {
      mode = "all";
      continue;
    }
    const ev = line.match(/^-\s*event:\s*([^#]+?)\s*$/);
    if (ev && mode) {
      const eventName = ev[1].trim();
      // lookahead for optional target and tolerance
      let target: string | undefined;
      let tolerance: number | undefined;
      for (let j = i + 1; j < Math.min(i + 6, lines.length); j++) {
        const t = lines[j].match(/^-?\s*target:\s*([^#]+?)\s*$/);
        if (t) {
          target = t[1].trim();
          continue;
        }
        const tol = lines[j].match(/^-?\s*tolerance:\s*([^#]+?)\s*$/);
        if (tol) {
          const num = Number(tol[1].trim());
          if (!Number.isNaN(num)) tolerance = num;
          continue;
        }
        if (/^[a-zA-Z]+:/.test(lines[j]) || /^-\s*event:/.test(lines[j])) break;
      }
      const trigger: StepEventTrigger = { event: eventName, target, tolerance };
      if (mode === "any") any.push(trigger);
      if (mode === "all") all.push(trigger);
    }
  }
  return any.length || all.length
    ? { any: any.length ? any : undefined, all: all.length ? all : undefined }
    : undefined;
}
